import { CartService, ListResponse } from "@gen/travel/v1/cart_pb";
import { client } from "./config";
import { useBookingStore } from "@stores/useBookingStore";
import {
  Passenger,
  PassengerSchema,
  Travel,
  UnnamedPassenger,
  UnnamedPassengerSchema,
} from "@gen/travel/v1/common_pb";
import { create } from "@bufbuild/protobuf";
import { sameName } from "./travel";

export async function getCart(): Promise<ListResponse> {
  return await client(CartService).list({});
}

export async function addToCart(dapNumber: string) {
  const outwardOffer = useBookingStore.getState().outwardOffer;
  if (!outwardOffer) {
    throw new Error("cannot add to cart: outward offer is null");
  }

  const inwardOffer = useBookingStore.getState().inwardOffer;
  if (!inwardOffer) {
    throw new Error("cannot add to cart: inward offer is null");
  }

  const results = useBookingStore.getState().inwardResults;
  if (!results) {
    throw new Error("cannot add to cart: inward results is null");
  }

  const request = useBookingStore.getState().searchRequest;
  if (!request) {
    throw new Error("cannot add to cart: search request is null");
  }

  const passports = useBookingStore.getState().passports;

  let mainPassenger: UnnamedPassenger | undefined;
  const additionalPassengers: Passenger[] = [];
  for (const passenger of results.passengers) {
    if (request.additionalPassengers?.find((p) => sameName(p, passenger))) {
      additionalPassengers.push(
        create(PassengerSchema, {
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          email: passenger.email,
          id: passenger.id,
          phoneNumber: passenger.phoneNumber,
          cards: passenger.cards,
          disability: passenger.disability,
          birthDate: passenger.birthDate,
          hasCompanionFare: passenger.hasCompanionFare,
          passport: passports.get(passenger.id),
        }),
      );
    } else {
      mainPassenger = create(UnnamedPassengerSchema, {
        birthDate: passenger.birthDate,
        email: passenger.email,
        id: passenger.id,
        phoneNumber: passenger.phoneNumber,
        cards: passenger.cards,
        disability: passenger.disability,
        hasCompanionFare: passenger.hasCompanionFare,
        passport: passports.get(passenger.id),
      });
    }
  }

  if (!mainPassenger) {
    throw new Error("cannot add to cart: main passenger not found");
  }

  await client(CartService).add({
    dapNumber,
    outward: {
      offerId: outwardOffer.offer.id,
      ancillaries: outwardOffer.ancillaries,
    },
    inward: {
      offerId: inwardOffer.offer.id,
      ancillaries: inwardOffer.ancillaries,
    },
    travelId: results.travelId,
    mainPassenger,
    additionalPassengers,
    placementWishes: useBookingStore.getState().placementWishes,
  });
}

export async function release(travel: Travel) {
  return await client(CartService).release({
    travelId: travel.id,
  });
}

export async function confirm(travel: Travel) {
  return await client(CartService).confirm({
    travelId: travel.id,
  });
}
